import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { TAG_GROUPS } from '@/features/discover/types/tags'
import { useEditorStore } from '@/features/editor/stores/editor-store'
import { SectionCard } from './section-card'

const OPTIONAL_GROUPS = ['genre', 'style', 'character'] as const

export function TagsSection() {
  const tags = useEditorStore((s) => s.draft.tags)
  const selectExclusiveTag = useEditorStore((s) => s.selectExclusiveTag)
  const toggleTag = useEditorStore((s) => s.toggleTag)
  const [optionalOpen, setOptionalOpen] = useState(false)
  const optionalId = 'optional-tags'

  return (
    <SectionCard
      title='标签'
      description='选择玩家身份、类型与题材风格标签（提交审核前需完成必选项）'
    >
      <div className='space-y-5'>
        <div className='space-y-2'>
          <div className='flex items-center gap-2 text-sm font-medium'>
            <span>玩家身份</span>
            <span className='text-muted-foreground text-xs'>必选</span>
          </div>
          <div className='flex flex-wrap gap-2'>
            {TAG_GROUPS.identity.tags.map((tag) => {
              const isSelected = tags.includes(tag)
              return (
                <Badge
                  key={tag}
                  variant={isSelected ? 'default' : 'secondary'}
                  className={cn(
                    'h-8 cursor-pointer rounded-full px-4 text-xs font-medium transition-all active:scale-95',
                    isSelected
                      ? 'bg-accent text-accent-foreground hover:bg-accent/90'
                      : 'bg-muted/40 hover:bg-muted/60 text-muted-foreground'
                  )}
                  onClick={() => selectExclusiveTag('identity', tag)}
                >
                  {tag}
                </Badge>
              )
            })}
          </div>
          <div className='text-muted-foreground space-y-1 text-xs leading-5'>
            <p>选择“固定主角”则个人资料中的“自定主角”功能不生效。</p>
            <p>
              如您选择“固定主角”，您必须在角色档案中增加对
              {'“玩家角色{{user}}”'}的定义条目。
            </p>
            <p>
              如您选择“自定主角”，您则无需在角色档案中对
              {'“玩家角色{{user}}”'}进行定义。
            </p>
          </div>
        </div>

        <div className='space-y-2'>
          <div className='flex items-center gap-2 text-sm font-medium'>
            <span>类型</span>
            <span className='text-muted-foreground text-xs'>必选</span>
          </div>
          <div className='flex flex-wrap gap-2'>
            {TAG_GROUPS.orientation.tags.map((tag) => {
              const isSelected = tags.includes(tag)
              return (
                <Badge
                  key={tag}
                  variant={isSelected ? 'default' : 'secondary'}
                  className={cn(
                    'h-8 cursor-pointer rounded-full px-4 text-xs font-medium transition-all active:scale-95',
                    isSelected
                      ? 'bg-accent text-accent-foreground hover:bg-accent/90'
                      : 'bg-muted/40 hover:bg-muted/60 text-muted-foreground'
                  )}
                  onClick={() => selectExclusiveTag('orientation', tag)}
                >
                  {tag}
                </Badge>
              )
            })}
          </div>
        </div>

        <div className='space-y-2'>
          <div className='flex items-center gap-2 text-sm font-medium'>
            <span>左右位</span>
            <span className='text-muted-foreground text-xs'>可选</span>
          </div>
          <div className='flex flex-wrap gap-2'>
            {TAG_GROUPS.position.tags.map((tag) => {
              const isSelected = tags.includes(tag)
              return (
                <Badge
                  key={tag}
                  variant={isSelected ? 'default' : 'secondary'}
                  className={cn(
                    'h-8 cursor-pointer rounded-full px-4 text-xs font-medium transition-all active:scale-95',
                    isSelected
                      ? 'bg-accent text-accent-foreground hover:bg-accent/90'
                      : 'bg-muted/40 hover:bg-muted/60 text-muted-foreground'
                  )}
                  onClick={() => selectExclusiveTag('position', tag)}
                >
                  {tag}
                </Badge>
              )
            })}
          </div>
        </div>

        <div className='space-y-4'>
          <div className='flex items-center justify-between gap-3'>
            <div className='text-sm font-medium'>题材/风格标签（可多选）</div>
            <button
              type='button'
              aria-expanded={optionalOpen}
              aria-controls={optionalId}
              onClick={() => setOptionalOpen((prev) => !prev)}
              className='text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs transition'
            >
              {optionalOpen ? '收起' : '更多'}
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 transition-transform duration-200',
                  optionalOpen ? 'rotate-180' : 'rotate-0'
                )}
              />
            </button>
          </div>

          <div
            id={optionalId}
            className={cn(
              'transition-[max-height,opacity] duration-200 ease-out',
              optionalOpen
                ? 'max-h-[60vh] overflow-y-auto opacity-100'
                : 'max-h-28 overflow-hidden opacity-90'
            )}
          >
            <div className='space-y-4'>
              {OPTIONAL_GROUPS.map((key) => {
                const group = TAG_GROUPS[key]
                return (
                  <div key={key} className='space-y-2'>
                    <div className='text-muted-foreground text-xs font-medium'>
                      {group.label}
                    </div>
                    <div className='flex flex-wrap gap-2'>
                      {group.tags.map((tag) => {
                        const isSelected = tags.includes(tag)
                        return (
                          <Badge
                            key={tag}
                            variant={isSelected ? 'default' : 'secondary'}
                            className={cn(
                              'h-8 cursor-pointer rounded-full px-4 text-xs font-medium transition-all active:scale-95',
                              isSelected
                                ? 'bg-accent text-accent-foreground hover:bg-accent/90'
                                : 'bg-muted/40 hover:bg-muted/60 text-muted-foreground'
                            )}
                            onClick={() => toggleTag(tag)}
                          >
                            {tag}
                          </Badge>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  )
}
